import React, { useState } from "react";
import { Box, Grid, Card, CardActionArea, CardContent, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField } from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import SecurityIcon from "@mui/icons-material/Security";
import LanguageIcon from "@mui/icons-material/Language";
import LocalSettingsPage from "./LocalSettingsPage";
import LanguageSelector from "../components/LanguageSelector";
import { useTranslation } from "react-i18next";
import AppsIcon from "@mui/icons-material/Apps";
import StoreAppsPage from "./StoreAppsPage";
import ApplicationSettingsPage from "./ApplicationSettingsPage";
import AppGroupApplicationsPage from "./AppGroupApplicationsPage";
import StorageIcon from "@mui/icons-material/Storage";
import SaveIcon from "@mui/icons-material/Save";
import UploadIcon from "@mui/icons-material/Upload";
import DownloadIcon from "@mui/icons-material/Download";
import { useNavigate } from "react-router-dom";
import BackupSettingsPage from "./BackupSettingsPage"; // ajout de l'import de BackupSettingsPage
import AppearanceIconsManager from './AppearanceIconsManager';
import IAConfigDialog from '../components/IAConfigDialog';
import AssociationSettingsPage from "./AssociationSettingsPage"; // ajout de l'import de AssociationSettingsPage
import SecuritySettingsPage from "./SecuritySettingsPage";
import { useMediaQuery } from "@mui/material";

// Nouvelle fonction utilitaire pour filtrer les cartes selon le rôle
function getVisibleSettings(role) {
  const all = [
    "local",
    "users",
    "groups",
    "desktops",
    "appearance",
    "security",
    "database",
    "application",
    "networkDrives",
    "backup",
    "explorer",
    "ia-config",
    "association",
    "store-apps"
  ];
  const superuser = [
    "local",
    "users",
    "groups",
    "desktops",
    "appearance",
    "application",
    "networkDrives",
    "backup",
    "explorer",
    "ia-config",
    "association",
    "store-apps"
  ];
  const user = ["ia-config"];
  if (role === "admin") return all;
  if (role === "superuser") return superuser;
  return user;
}

export default function SettingsPage({ onOpenApp, currentUser = null, groupId }) {
  const { t } = useTranslation();
  // Use role from currentUser, fallback to 'user' if not present
  const role = currentUser?.role || "user";
  const visibleSettings = getVisibleSettings(role);
  const navigate = useNavigate();
  const [appearanceIconsOpen, setAppearanceIconsOpen] = useState(false);
  const [iaConfigOpen, setIaConfigOpen] = useState(false);
  const [iaConfig, setIaConfig] = useState({});
  const [associationOpen, setAssociationOpen] = useState(false);
  const [securityOpen, setSecurityOpen] = useState(false);
  const [storeAppsOpen, setStoreAppsOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width:600px)');

  // settings doit être ici pour accéder à setAppearanceIconsOpen
  const settings = [
    {
      key: "store-apps",
      labelKey: "settings.store_apps",
      icon: <AppsIcon fontSize="large" color="primary" />,
      descriptionKey: "settings.store_apps.desc",
      onOpen: () => setStoreAppsOpen(true)
    },
    {
      key: "local",
      labelKey: "settings.local",
      icon: <LanguageIcon fontSize="large" color="primary" />,
      descriptionKey: "settings.local.desc",
      onOpen: "local-settings"
    },
    {
      key: "users",
      labelKey: "settings.users",
      icon: <PeopleIcon fontSize="large" color="primary" />,
      descriptionKey: "settings.users.desc",
      onOpen: "users"
    },
    {
      key: "groups",
      labelKey: "settings.groups",
      icon: <DesktopWindowsIcon fontSize="large" color="primary" />,
      descriptionKey: "settings.groups.desc",
      onOpen: "groups"
    },
    {
      key: "desktops",
      labelKey: "settings.desktops",
      icon: <DesktopWindowsIcon fontSize="large" color="primary" />,
      descriptionKey: "settings.desktops.desc",
      onOpen: "app-group-applications"
    },
    {
      key: "appearance",
      labelKey: "settings.appearance",
      icon: <ColorLensIcon fontSize="large" color="primary" />,
      descriptionKey: "settings.appearance.desc",
      onOpen: () => setAppearanceIconsOpen(true)
    },
    {
      key: "security",
      labelKey: "settings.security",
      icon: <SecurityIcon fontSize="large" color="primary" />,
      descriptionKey: "settings.security.desc",
      onOpen: () => setSecurityOpen(true)
    },
    {
      key: "application",
      labelKey: "settings.application",
      icon: <AppsIcon fontSize="large" color="primary" />,
      descriptionKey: "settings.application.desc",
      onOpen: "application-settings"
    },
    {
      key: "networkDrives",
      labelKey: "settings.networkDrives",
      icon: <StorageIcon fontSize="large" color="secondary" />,
      descriptionKey: "settings.networkDrives.desc",
      onOpen: "network-drives"
    },
    {
      key: "explorer",
      labelKey: "settings.explorer",
      icon: <AppsIcon fontSize="large" color="primary" />,
      descriptionKey: "settings.explorer.desc",
      onOpen: "explorer"
    },
    {
      key: "backup",
      labelKey: "settings.backup",
      icon: <SaveIcon fontSize="large" color="primary" />,
      descriptionKey: "settings.backup.desc",
      onOpen: "backup-settings"
    },
    {
      key: "association",
      labelKey: "settings.association",
      icon: <SaveIcon fontSize="large" color="primary" />,
      descriptionKey: "settings.association.desc",
      onOpen: () => { if (isMobile) setAssociationOpen(true); else onOpenApp("association-settings", { currentUser, groupId }); }
    },
    {
      key: "database",
      labelKey: "settings.database",
      icon: <StorageIcon fontSize="large" color="primary" />,
      descriptionKey: "settings.database.desc",
      onOpen: "database-config"
    },
    {
      key: "ia-config",
      labelKey: "settings.ia-config",
      icon: <DesktopWindowsIcon fontSize="large" color="secondary" />,
      descriptionKey: "settings.ia-config.desc",
      onOpen: () => { if (isMobile) setIaConfigOpen(true); else onOpenApp("ia-config", { currentUser, groupId }); }
    },
  ];

  return (
    <Box sx={{ height: '100%', overflow: 'auto', boxSizing: 'border-box', p: 4, maxHeight: '70vh' }}>
      <Typography variant="h4" mb={4}>{t('settings.title')}</Typography>
      <Box mb={2}>
        <LanguageSelector />
      </Box>
      <Grid container spacing={3}>
        {settings.filter(s => visibleSettings.includes(s.key)).map(setting => (
          <Grid item xs={12} sm={6} md={3} key={setting.key}>
            <Card>
              <CardActionArea
                onClick={() => {
                  if (typeof setting.onOpen === 'function') setting.onOpen();
                  else if (onOpenApp && typeof setting.onOpen === 'string') onOpenApp(setting.onOpen);
                }}
                onTouchStart={e => {
                  // Fix for mobile: ensure onOpen is called on touch devices
                  e.stopPropagation();
                  if (typeof setting.onOpen === 'function') setting.onOpen();
                  else if (onOpenApp && typeof setting.onOpen === 'string') onOpenApp(setting.onOpen);
                }}
              >
                <CardContent sx={{ textAlign: "center", py: 3 }}>
                  {setting.icon}
                  <Typography variant="h6">{t(setting.labelKey)}</Typography>
                  <Typography variant="body2" color="text.secondary">{t(setting.descriptionKey)}</Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
      {appearanceIconsOpen && (
        <Dialog open={appearanceIconsOpen} onClose={() => setAppearanceIconsOpen(false)} maxWidth="md" fullWidth title={t('settings.open')}>
          <DialogTitle>{t('settings.appearance.title')}</DialogTitle>
          <DialogContent>
            <AppearanceIconsManager />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAppearanceIconsOpen(false)}>{t('close')}</Button>
          </DialogActions>
        </Dialog>
      )}
      {iaConfigOpen && (
        <Dialog open={iaConfigOpen} onClose={() => setIaConfigOpen(false)} maxWidth="sm" fullWidth title={t('settings.open')}>
          <Box sx={{ p: 2 }}>
            <DialogTitle sx={{ pb: 0 }}>{t('settings.ia-config.title')}</DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
              <IAConfigDialog
                open={iaConfigOpen}
                onClose={() => setIaConfigOpen(false)}
                initialConfig={iaConfig}
                onSave={setIaConfig}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setIaConfigOpen(false)} variant="outlined">{t('close')}</Button>
            </DialogActions>
          </Box>
        </Dialog>
      )}
      {associationOpen && (
        <Dialog open={associationOpen} onClose={() => setAssociationOpen(false)} maxWidth="sm" fullWidth title={t('settings.open')}>
          <Box sx={{ p: 2 }}>
            <DialogTitle sx={{ pb: 0 }}>{t('settings.association')}</DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
              <AssociationSettingsPage currentUser={currentUser} groupId={groupId} />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAssociationOpen(false)} variant="outlined">{t('close')}</Button>
            </DialogActions>
          </Box>
        </Dialog>
      )}
      {securityOpen && (
        <Dialog open={securityOpen} onClose={() => setSecurityOpen(false)} maxWidth="sm" fullWidth title={t('settings.open')}>
          <Box sx={{ p: 2 }}>
            <DialogTitle sx={{ pb: 0 }}>{t('settings.security')}</DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
              <SecuritySettingsPage currentUser={currentUser} />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSecurityOpen(false)} variant="outlined">{t('close')}</Button>
            </DialogActions>
          </Box>
        </Dialog>
      )}
      {storeAppsOpen && (
        <Dialog open={storeAppsOpen} onClose={() => setStoreAppsOpen(false)} maxWidth="md" fullWidth title={t('settings.open')}>
          <DialogTitle>{t('store.title')}</DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <StoreAppsPage />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStoreAppsOpen(false)} variant="outlined">{t('close')}</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}
